import { useQueryErrorResetBoundary } from '@tanstack/react-query';
import type { CellContext, ColumnDef } from '@tanstack/react-table';
import type { ServiceCallError } from 'api/ServiceCallError';
import clsx from 'clsx';
import type { UnresolvedCommitDescriptor } from 'custom-types/UnresolvedCommitDescriptor';
import type { ReactNode } from 'react';
import { type JSX, Suspense, useEffect, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useProjectId } from 'ts/base/hooks/UseProject';
import type { TgaCoverageSources } from 'ts/base/routing/search/schema/TgaCoverageSources';
import { TeamscaleLink } from 'ts/base/routing/TeamscaleLink';
import { useIssueQueryResult } from 'ts/base/services/IssueQueryServices';
import { RemoteSortableTable } from 'ts/base/table/RemoteSortableTable';
import { DateUtils } from 'ts/commons/DateUtils';
import { ErrorFallback } from 'ts/commons/ErrorFallback';
import { IssueStatusLabel } from 'ts/commons/IssueStatusLabel';
import { EIssueLikeQueryableEntityTypeField } from 'ts/commons/query/EIssueLikeQueryableEntityTypeField';
import type { IssueLikeQueryableEntityType } from 'ts/commons/query/QueryableEntityType';
import { QueryableEntities } from 'ts/commons/query/QueryableEntityType';
import { QueryParserFailureMessage } from 'ts/commons/query/QueryParserFailureMessage';
import { StringUtils } from 'ts/commons/StringUtils';
import { getColumnId, useDeriveHiddenColumns } from 'ts/commons/TableUtils';
import { Icon } from 'ts/components/Icon';
import { Loader } from 'ts/components/Loader';
import { useIssueQueryContext } from 'ts/perspectives/activity/issues/overview/IssueQueryContext';
import { LinkedIssuesLinks } from 'ts/perspectives/requirement_tracing/spec_item_detail/components/LinkedIssuesSection';
import { TypeAbbreviationLabel } from 'ts/perspectives/requirement_tracing/spec_item_detail/components/TypeAbbreviationLabel';
import styles from 'ts/perspectives/requirement_tracing/views/SpecItemsReferenceMatrix.module.less';
import { RoundAvatar } from 'ts/perspectives/user/RoundAvatar';
import type { ImportedLinksAndTypeResolvedSpecItem } from 'typedefs/ImportedLinksAndTypeResolvedSpecItem';
import type { IssueTgaParameters } from 'typedefs/IssueTgaParameters';
import type { QueryParserFailure } from 'typedefs/QueryParserFailure';
import type { ReferenceResolvedSpecItem } from 'typedefs/ReferenceResolvedSpecItem';
import type { UserResolvedTeamscaleIssue } from 'typedefs/UserResolvedTeamscaleIssue';

/** The DTOs exposed from the backend for either issues or spec items. */
export type IssueLike = UserResolvedTeamscaleIssue | ImportedLinksAndTypeResolvedSpecItem;

/** Provides suspense handling and error boundary for issue table like components. */
export function IssueQueryErrorSuspenseBoundary({ children }: { children: ReactNode }): JSX.Element {
	const { reset } = useQueryErrorResetBoundary();
	return (
		<Suspense fallback={<Loader active inline="centered" />}>
			<ErrorBoundary onReset={reset} FallbackComponent={IssueQueryError}>
				{children}
			</ErrorBoundary>
		</Suspense>
	);
}

/** Props for IssueQueryError. */
type IssueQueryErrorProps = {
	error: unknown;
	resetErrorBoundary?: () => void;
};

/** Handle errors resulting from requesting issue data from server. */
export function IssueQueryError({ error, resetErrorBoundary }: IssueQueryErrorProps): JSX.Element {
	const { reset, isReset } = useQueryErrorResetBoundary();
	const isActuallyReset = isReset();
	useEffect(() => {
		if (resetErrorBoundary && isActuallyReset) {
			resetErrorBoundary();
		}
	}, [resetErrorBoundary, isActuallyReset]);
	const fullDescription = (error as ServiceCallError).fullDescription;
	if (fullDescription == null) {
		// Request timeout or failed connection
		// This may happen if we quickly switch to another page before the request finishes
		return <ErrorFallback error={error} resetErrorBoundary={reset} />;
	}
	return <QueryParserFailureMessage queryParserFailure={fullDescription as QueryParserFailure} />;
}

/** Loads issues or spec items based on a query and shows a table with these data. */
export function IssueLikeQueryResultsTable(): JSX.Element {
	const { issueLikeType, activeQuery, sortOptions, setSortOptions, visibleColumns, commit } = useIssueQueryContext();
	const { data, isPending, error, fetchNextPage, hasNextPage } = useIssueQueryResult(
		activeQuery,
		sortOptions,
		issueLikeType,
		undefined,
		true
	);
	const columns = useIssueLikeQueryableEntityColumns(issueLikeType, visibleColumns, undefined, undefined, commit);
	const columnVisibility = useDeriveHiddenColumns(columns, visibleColumns);
	const flatData = useMemo(() => {
		if (isPending || error) {
			return [];
		}
		return data.pages.flatMap(page => page.issues);
	}, [data, isPending, error]);

	if (isPending) {
		return (
			<Loader active inline="centered" data-testid="query-loader">
				{StringUtils.isEmptyOrWhitespace(activeQuery)
					? `Loading ${issueLikeType.capitalizedName}s`
					: `Loading ${issueLikeType.capitalizedName} Query Results`}
			</Loader>
		);
	}
	if (error) {
		return <IssueQueryError error={error} />;
	}
	return (
		<RemoteSortableTable
			selectable
			striped
			unstackable
			sorting={sortOptions}
			columnDefinitions={columns}
			data={flatData}
			onSortOptionsChanged={setSortOptions}
			onFetchNext={fetchNextPage}
			hasNextPage={hasNextPage}
			data-testid="mark-issues-table"
			className={clsx('framed', styles.stickyHeaderLessMarginTop)}
			tableOptions={{
				enableHiding: true,
				state: {
					columnVisibility
				}
			}}
		/>
	);
}

function getIssueValueForColumn(resolvedIssue: IssueLike, column: string) {
	const additionalFieldNamesIndex = resolvedIssue.issue.additionalFieldNames.indexOf(column);
	if (additionalFieldNamesIndex >= 0) {
		return resolvedIssue.issue.additionalFieldValues[additionalFieldNamesIndex];
	} else if (isSpecItem(resolvedIssue)) {
		// Not a field, so maybe a spec item link role?
		const linkRolesIndex = resolvedIssue.issue.linkRoles.indexOf(column);
		if (linkRolesIndex >= 0) {
			return resolvedIssue.issue.linkedSpecItems[linkRolesIndex];
		}

		// User resolution information
		if (column in resolvedIssue.specItemUserResolution) {
			return resolvedIssue.specItemUserResolution[column as keyof typeof resolvedIssue.specItemUserResolution];
		}

		// Links and type resolution information
		if (column in resolvedIssue.linksAndTypeResolution) {
			return resolvedIssue.linksAndTypeResolution[column as keyof typeof resolvedIssue.linksAndTypeResolution];
		}

		// Reference resolution information
		if (column in resolvedIssue.referenceResolution) {
			return resolvedIssue.referenceResolution[column as keyof typeof resolvedIssue.referenceResolution];
		}
	}
	// Unknown, so default to empty
	return null;
}

function getIssueCellForColumn(resolvedIssue: IssueLike, column: string): JSX.Element | null {
	if (isSpecItem(resolvedIssue)) {
		const result = getIssueCellForSpecItem(resolvedIssue, column);
		if (result !== null) {
			return result;
		}
	}
	const additionalFieldNamesIndex = resolvedIssue.issue.additionalFieldNames.indexOf(column);
	if (additionalFieldNamesIndex >= 0) {
		return <>{resolvedIssue.issue.additionalFieldValues[additionalFieldNamesIndex]}</>;
	}
	// Unknown, so default to empty
	return null;
}

function getIssueCellForSpecItem(resolvedIssue: ReferenceResolvedSpecItem, column: string): JSX.Element | null {
	if (column in resolvedIssue.linksAndTypeResolution.resolvedRequirementLinks) {
		return (
			<LinkedIssuesLinks
				linkedIssueIds={resolvedIssue.linksAndTypeResolution.resolvedRequirementLinks[column]!}
			/>
		);
	}

	// Reference resolution information
	if (column in resolvedIssue.referenceResolution) {
		return <>{resolvedIssue.referenceResolution[column as keyof typeof resolvedIssue.referenceResolution]}</>;
	}
	return null;
}

/** Defines the column and cell implementation for the issues or spec items table. */
export function useIssueLikeQueryableEntityColumns<Issue extends IssueLike>(
	issueLikeType: IssueLikeQueryableEntityType,
	visibleColumns: string[] = [],
	issueTgaParameters?: IssueTgaParameters,
	tgaCoverageSources?: TgaCoverageSources,
	commit?: UnresolvedCommitDescriptor
): Array<ColumnDef<Issue>> {
	// We store the current timestamp once and pass this value to the avatar requests
	// to prevent reloading them on every re-rendering, see TS-39247.
	const [cacheInvalidationTimestamp] = useState(Date.now);
	return useMemo(() => {
		const defaultColumns: Array<ColumnDef<Issue>> = [
			{
				header: 'ID',
				id: EIssueLikeQueryableEntityTypeField.ID,
				accessorFn: (resolvedIssue: Issue) => resolvedIssue.issue.id,
				cell({ row }: CellContext<Issue, unknown>): JSX.Element {
					const entry: Issue = row.original;
					return (
						<IssueLikeIdLink
							resolvedIssue={entry}
							issueLikeType={issueLikeType}
							selectedTestGapBranch={issueTgaParameters?.branchName}
							tgaCoverageSources={tgaCoverageSources}
							commit={commit}
						/>
					);
				},
				meta: {
					collapsing: true
				}
			},
			{
				header: 'Subject',
				id: EIssueLikeQueryableEntityTypeField.SUBJECT,
				accessorFn: (resolvedIssue: Issue) => resolvedIssue.issue.subject,
				cell({ row }: CellContext<Issue, unknown>): JSX.Element {
					const resolvedIssue: Issue = row.original;
					return (
						<IssueLikeSubjectLink
							issueLikeType={issueLikeType}
							resolvedIssue={resolvedIssue}
							selectedTestGapBranch={issueTgaParameters?.branchName}
							tgaCoverageSources={tgaCoverageSources}
							commit={commit}
						/>
					);
				}
			},
			{
				header: 'Status',
				id: EIssueLikeQueryableEntityTypeField.STATUS,
				accessorFn: (resolvedIssue: Issue) => resolvedIssue.issue.status,
				cell({ row }: CellContext<Issue, unknown>): JSX.Element {
					const resolvedIssue: Issue = row.original;
					return <IssueStatusLabel useBasicStyle issueStatus={resolvedIssue.issue.status} size="small" />;
				},
				meta: {
					collapsing: true,
					className: 'center aligned'
				}
			},
			{
				header: 'Assignee',
				id: EIssueLikeQueryableEntityTypeField.ASSIGNEE,
				accessorFn: (resolvedIssue: Issue) => resolvedIssue.issue.assignee,
				cell({ row }: CellContext<Issue, unknown>): JSX.Element {
					const resolvedIssue: Issue = row.original;
					return (
						<RoundAvatar
							hideUserName
							prefixForTooltip="Assignee:"
							user={resolvedIssue.issueUserResolution.resolvedAssignee}
							username={resolvedIssue.issue.assignee}
							cacheInvalidationTimestamp={cacheInvalidationTimestamp}
						/>
					);
				},
				meta: {
					collapsing: true
				}
			},
			{
				header: 'Author',
				id: EIssueLikeQueryableEntityTypeField.AUTHOR,
				accessorFn: (resolvedIssue: Issue) => resolvedIssue.issue.author,
				cell({ row }: CellContext<Issue, unknown>): JSX.Element {
					const resolvedIssue: Issue = row.original;
					return (
						<RoundAvatar
							hideUserName
							prefixForTooltip="Author:"
							user={resolvedIssue.issueUserResolution.resolvedAuthor}
							username={resolvedIssue.issue.author}
							cacheInvalidationTimestamp={cacheInvalidationTimestamp}
						/>
					);
				},
				meta: {
					collapsing: true
				}
			},
			{
				header: 'Parent',
				id: 'parent',
				accessorFn: (resolvedIssue: Issue) => resolvedIssue.issue.parentId,
				cell({ row }: CellContext<Issue, unknown>): JSX.Element | null {
					const entry: Issue = row.original;
					if (entry.issue.parentId == null) {
						return null;
					}
					return (
						<IssueLikeIdLink
							resolvedIssue={entry}
							issueLikeType={issueLikeType}
							selectedTestGapBranch={issueTgaParameters?.branchName}
							tgaCoverageSources={tgaCoverageSources}
							commit={commit}
						/>
					);
				},
				meta: {
					collapsing: true
				}
			},
			{
				header: 'Closed',
				id: EIssueLikeQueryableEntityTypeField.CLOSED,
				accessorFn: (resolvedIssue: Issue) => resolvedIssue.issue.closed,
				cell({ row }: CellContext<Issue, unknown>): JSX.Element {
					const entry: Issue = row.original;
					return <>{entry.issue.closed ? 'Closed' : 'Open'}</>;
				},
				meta: {
					collapsing: true
				}
			},
			{
				header: 'Created',
				id: EIssueLikeQueryableEntityTypeField.CREATED,
				accessorFn: (resolvedIssue: Issue) => resolvedIssue.issue.created,
				cell({ row }: CellContext<Issue, unknown>): JSX.Element {
					const entry: Issue = row.original;
					return <>{DateUtils.formatTimestamp(entry.issue.created)}</>;
				},
				meta: {
					collapsing: true
				}
			},
			{
				header: 'Updated',
				id: EIssueLikeQueryableEntityTypeField.UPDATED,
				accessorFn: (resolvedIssue: Issue) => resolvedIssue.issue.updated,
				cell({ row }: CellContext<Issue, unknown>): JSX.Element {
					const entry: Issue = row.original;
					return <>{DateUtils.formatTimestamp(entry.issue.updated)}</>;
				},
				meta: {
					collapsing: true
				}
			},
			{
				header: 'Type',
				id: EIssueLikeQueryableEntityTypeField.TYPE,
				accessorFn: (resolvedIssue: Issue) => {
					if ('typeInfo' in resolvedIssue.issue) {
						return resolvedIssue.issue.typeInfo?.type;
					}
					return null;
				},
				meta: {
					collapsing: true
				}
			},
			{
				header: 'Connector',
				id: EIssueLikeQueryableEntityTypeField.CONNECTOR,
				accessorFn: (resolvedIssue: Issue) => resolvedIssue.issue.id.connectorId,
				meta: {
					collapsing: true
				}
			}
		];
		const additionalColumns = visibleColumns
			.filter(columnName => defaultColumns.findIndex(column => getColumnId(column) === columnName) === -1)
			.map(column => {
				return {
					header: column.charAt(0).toUpperCase() + column.substring(1),
					id: column,
					accessorFn: (resolvedIssue: Issue) => getIssueValueForColumn(resolvedIssue, column),
					cell({ row }: CellContext<Issue, unknown>): JSX.Element | null {
						return getIssueCellForColumn(row.original, column);
					},
					meta: {
						collapsing: true
					}
				};
			});
		return [...defaultColumns, ...additionalColumns];
	}, [
		visibleColumns,
		issueLikeType,
		issueTgaParameters?.branchName,
		tgaCoverageSources,
		commit,
		cacheInvalidationTimestamp
	]);
}

/** Checks whether the given IssueLike is a SpecItem */
function isSpecItem(issueLike: IssueLike): issueLike is ReferenceResolvedSpecItem {
	return 'referenceResolution' in issueLike;
}

/** Props for IssueLikeIdLink. */
type IssueLikeIdLinkProps = {
	resolvedIssue: IssueLike;
	issueLikeType: IssueLikeQueryableEntityType;
	selectedTestGapBranch?: null | string;
	tgaCoverageSources?: TgaCoverageSources;
	commit?: UnresolvedCommitDescriptor;
};

/**
 * Shows a like either to the external page from where the issue-like entry has been imported or to the detail page of
 * the issue-like item in case no extern URL is available.
 */
function IssueLikeIdLink({
	resolvedIssue,
	issueLikeType,
	selectedTestGapBranch,
	tgaCoverageSources,
	commit
}: IssueLikeIdLinkProps): JSX.Element {
	const { issue } = resolvedIssue;
	const projectId = useProjectId();
	if (!StringUtils.isEmptyOrWhitespace(issue.url)) {
		return (
			<a target="_blank" rel="noreferrer" href={issue.url}>
				<Icon name="external alternate" /> {issue.id.externalId}
			</a>
		);
	} else {
		return (
			<TeamscaleLink
				to={issueLikeType.getEntityLink(projectId, issue.id, commit, selectedTestGapBranch, tgaCoverageSources)}
			>
				{issue.id.externalId}
			</TeamscaleLink>
		);
	}
}

/** Props for IssueLikeSubjectLink. */
type IssueLikeSubjectLinkProps = {
	resolvedIssue: IssueLike;
	issueLikeType: IssueLikeQueryableEntityType;
	selectedTestGapBranch?: null | string;
	tgaCoverageSources?: TgaCoverageSources;
	commit?: UnresolvedCommitDescriptor;
};

/** Link to the issue-like entity's detail page. */
function IssueLikeSubjectLink({
	resolvedIssue,
	issueLikeType,
	selectedTestGapBranch,
	tgaCoverageSources,
	commit
}: IssueLikeSubjectLinkProps): JSX.Element {
	const { issue } = resolvedIssue;
	const projectId = useProjectId();
	if (issueLikeType === QueryableEntities.SPEC_ITEM && 'typeInfo' in issue) {
		return (
			<TeamscaleLink
				to={issueLikeType.getEntityLink(projectId, issue.id, commit, selectedTestGapBranch, tgaCoverageSources)}
			>
				<TypeAbbreviationLabel typeInfo={issue.typeInfo} />
				{' ' + issue.subject}
			</TeamscaleLink>
		);
	}
	return (
		<TeamscaleLink
			to={issueLikeType.getEntityLink(projectId, issue.id, commit, selectedTestGapBranch, tgaCoverageSources)}
		>
			{issue.subject}
		</TeamscaleLink>
	);
}
